import { escapeHtml } from '../../../modules/files/file-list.utils.js';

// Summary popup state
let summaryPopupElement = null;
let summaryPopupTimeout = null;
let summaryHoverTimeout = null;
let currentSummaryFile = null;
let summaryPopupHovered = false;
let summaryIconHovered = false;

// Summary popup configuration
const SUMMARY_POPUP_HIDE_DELAY = 1000;
const SUMMARY_POPUP_HOVER_DELAY = 500; // Delay before showing popup on hover
const SUMMARY_POSITION_COOKIE_NAME = 'summary_popup_position';

// Cookie helpers for summary popup position
function saveSummaryPosition(left, top) {
	try {
		const position = JSON.stringify({ left, top });
		const expires = new Date();
		expires.setFullYear(expires.getFullYear() + 1);
		document.cookie = `${SUMMARY_POSITION_COOKIE_NAME}=${encodeURIComponent(position)}; expires=${expires.toUTCString()}; path=/`;
	} catch (e) {
		console.warn('[Summary] Failed to save position to cookie:', e);
	}
}

function loadSummaryPosition() {
	try {
		const cookies = document.cookie.split(';');
		for (let cookie of cookies) {
			cookie = cookie.trim();
			if (cookie.startsWith(SUMMARY_POSITION_COOKIE_NAME + '=')) {
				const value = decodeURIComponent(cookie.substring(SUMMARY_POSITION_COOKIE_NAME.length + 1));
				const position = JSON.parse(value);
				if (position && typeof position.left === 'number' && typeof position.top === 'number') {
					return position;
				}
			}
		}
	} catch (e) {
		console.warn('[Summary] Failed to load position from cookie:', e);
	}
	return null;
}

function updateSummaryContent(file, fileKey, summary) {
	if (!summaryPopupElement || !summaryPopupElement.length) return;
	
	const fileName = file.n || file.name || 'Unknown';
	
	// Update title
	summaryPopupElement.find('.summary-popup__title').text('AI summary of the document');
	
	// Update content
	summaryPopupElement.find('.summary-popup__text').html(escapeHtml(summary));
	
	// Update current file reference (preserve $icon if exists)
	currentSummaryFile = { file, fileKey, $icon: currentSummaryFile?.$icon };
}

async function showSummaryPopup(file, fileKey, $icon) {
	const { openDatabase, closeDatabase, getSummariesByKeys } = await import('../../storage/idb-files.js');
	
	try {
		const db = await openDatabase();
		
		// Try multiple key variations (like fulltext search does)
		const fileName = file.n || file.name || '';
		const filePath = file.p || file.path || '';
		const nameKey = fileName || fileKey.split('/').pop() || '';
		
		// Try fileKey first (full path)
		let summaries = await getSummariesByKeys(db, [fileKey]);
		
		// If not found, try nameKey
		if (!summaries || !summaries[0]) {
			summaries = await getSummariesByKeys(db, [nameKey]);
		}
		
		// If still not found, try filePath
		if ((!summaries || !summaries[0]) && filePath && filePath !== fileKey) {
			summaries = await getSummariesByKeys(db, [filePath]);
		}
		
		closeDatabase(db);
		
		const summary = summaries && summaries[0];
		if (!summary) {
			showSummaryErrorMessage('Summary not available for this file');
			return;
		}
		
		renderSummaryPopup(file, fileKey, summary, $icon);
	} catch (error) {
		console.error('[SummaryPopup] ❌ Error loading summary:', error);
		showSummaryErrorMessage('Failed to load summary');
	}
}

function renderSummaryPopup(file, fileKey, summary, $icon) {
	const fileName = file.n || file.name || 'Unknown';
	
	const isSwitching = summaryPopupElement && summaryPopupElement.length;
	
	if (isSwitching) {
		const rect = summaryPopupElement[0].getBoundingClientRect();
		saveSummaryPosition(rect.left, rect.top);
		hideSummaryPopup();
	} else {
		hideSummaryPopup();
	}
	
	const popupHtml = `
		<div class="summary-popup-overlay" id="summary-popup-overlay">
			<div class="summary-popup">
				<div class="summary-popup__header">
					<div class="summary-popup__title">AI summary of the document</div>
					<button class="summary-popup__close" aria-label="Close">
						<i class="bi bi-x-lg"></i>
					</button>
				</div>
				<div class="summary-popup__content">
					<div class="summary-popup__text">${escapeHtml(summary)}</div>
				</div>
			</div>
		</div>
	`;
	
	$('body').append(popupHtml);
	summaryPopupElement = $('#summary-popup-overlay');
	currentSummaryFile = { file, fileKey, $icon };
	
	const isMobile = window.innerWidth < 1200;
	const popupWidth = 500;
	const popupHeight = 400;
	
	let left, top;
	
	// On mobile, center the popup (modal style)
	if (isMobile) {
		// Mobile: centered modal, no positioning needed (handled by CSS)
		left = null;
		top = null;
	} else {
		// Desktop: position relative to icon
	// Always try to load saved position from cookies
	const savedPosition = loadSummaryPosition();
	
	// Get icon position for positioning popup to the left
	let iconX = null;
	let iconY = null;
	
	if ($icon && $icon.length) {
		const iconRect = $icon[0].getBoundingClientRect();
		iconX = iconRect.left + iconRect.width / 2;
		iconY = iconRect.top + iconRect.height / 2;
	}
	
	// If we have saved position, check if it covers icon
	if (savedPosition && iconX !== null && iconY !== null) {
		const savedLeft = savedPosition.left;
		const savedTop = savedPosition.top;
		const savedRight = savedLeft + popupWidth;
		const savedBottom = savedTop + popupHeight;
		
		// Check if icon is inside saved popup bounds
		const coversIcon = iconX >= savedLeft && iconX <= savedRight && 
		                   iconY >= savedTop && iconY <= savedBottom;
		
		if (coversIcon) {
			// Icon is covered - use default position (to the left of icon)
			if ($icon && $icon.length) {
				const iconRect = $icon[0].getBoundingClientRect();
				left = iconRect.left - popupWidth - 10; // 10px gap to the left
				top = iconRect.top + (iconRect.height / 2) - (popupHeight / 2);
				
				// Constrain to viewport
				if (left < 10) left = 10;
				if (top < 10) top = 10;
				if (top + popupHeight > window.innerHeight - 10) {
					top = window.innerHeight - popupHeight - 10;
				}
			} else {
				left = (window.innerWidth - popupWidth) / 2;
				top = (window.innerHeight - popupHeight) / 2;
			}
		} else {
			// Icon is not covered - use saved position
			const maxLeft = window.innerWidth - popupWidth;
			const maxTop = window.innerHeight - popupHeight;
			left = Math.max(0, Math.min(savedPosition.left, maxLeft));
			top = Math.max(0, Math.min(savedPosition.top, maxTop));
		}
	} else if (savedPosition) {
		// Have saved position but no icon info - use saved position
		const maxLeft = window.innerWidth - popupWidth;
		const maxTop = window.innerHeight - popupHeight;
		left = Math.max(0, Math.min(savedPosition.left, maxLeft));
		top = Math.max(0, Math.min(savedPosition.top, maxTop));
	} else {
		// No saved position - position to the left of icon
		if ($icon && $icon.length) {
			const iconRect = $icon[0].getBoundingClientRect();
			left = iconRect.left - popupWidth - 10; // 10px gap to the left
			top = iconRect.top + (iconRect.height / 2) - (popupHeight / 2);
			
			// Constrain to viewport
			if (left < 10) left = 10;
			if (top < 10) top = 10;
			if (top + popupHeight > window.innerHeight - 10) {
				top = window.innerHeight - popupHeight - 10;
			}
		} else {
			left = (window.innerWidth - popupWidth) / 2;
			top = (window.innerHeight - popupHeight) / 2;
		}
	}
	}
	
	// Apply positioning only on desktop
	if (!isMobile) {
		summaryPopupElement.css({
			position: 'fixed',
			left: left + 'px',
			top: top + 'px',
			width: popupWidth + 'px',
			height: popupHeight + 'px',
			background: 'transparent',
			backdropFilter: 'none'
		});
		summaryPopupElement.find('.summary-popup').css({
			width: '100%',
			height: '100%',
			margin: 0,
			maxWidth: 'none',
			maxHeight: 'none'
		});
	}
	
	const closePopup = () => {
		if (summaryPopupElement && summaryPopupElement.length) {
			const rect = summaryPopupElement[0].getBoundingClientRect();
			saveSummaryPosition(rect.left, rect.top);
		}
		$(document).off('mousemove.summary-drag mouseup.summary-drag');
		if (summaryPopupElement) {
			summaryPopupElement.remove();
			summaryPopupElement = null;
			currentSummaryFile = null;
		}
		if (summaryPopupTimeout) {
			clearTimeout(summaryPopupTimeout);
			summaryPopupTimeout = null;
		}
	};
	
	summaryPopupElement.find('.summary-popup__close').on('click', closePopup);
	
	// On mobile, close popup when clicking on overlay
	if (isMobile) {
		summaryPopupElement.on('click', function(e) {
			if (e.target === this) {
				closePopup();
			}
		});
	}
	
	const header = summaryPopupElement.find('.summary-popup__header');
	let isDragging = false;
	let dragStartX = 0;
	let dragStartY = 0;
	let popupStartLeft = 0;
	let popupStartTop = 0;
	
	// Only enable dragging on desktop
	if (!isMobile) {
	header.on('mousedown', function(e) {
		if ($(e.target).closest('.summary-popup__close').length) {
			return;
		}
		isDragging = true;
		const popupRect = summaryPopupElement[0].getBoundingClientRect();
		dragStartX = e.clientX;
		dragStartY = e.clientY;
		popupStartLeft = popupRect.left;
		popupStartTop = popupRect.top;
		e.preventDefault();
		summaryPopupElement.addClass('summary-popup--dragging');
	});
	
	$(document).on('mousemove.summary-drag', function(e) {
		if (!isDragging || !summaryPopupElement || !summaryPopupElement.length) return;
		const deltaX = e.clientX - dragStartX;
		const deltaY = e.clientY - dragStartY;
		const newLeft = popupStartLeft + deltaX;
		const newTop = popupStartTop + deltaY;
		const popupWidth = summaryPopupElement.outerWidth();
		const popupHeight = summaryPopupElement.outerHeight();
		const maxLeft = window.innerWidth - popupWidth;
		const maxTop = window.innerHeight - popupHeight;
		const constrainedLeft = Math.max(0, Math.min(newLeft, maxLeft));
		const constrainedTop = Math.max(0, Math.min(newTop, maxTop));
		summaryPopupElement.css({
			left: constrainedLeft + 'px',
			top: constrainedTop + 'px'
		});
		saveSummaryPosition(constrainedLeft, constrainedTop);
	});
	
	$(document).on('mouseup.summary-drag', function() {
		if (isDragging) {
			isDragging = false;
			summaryPopupElement.removeClass('summary-popup--dragging');
		}
	});
	}
	
	summaryPopupElement.on('mouseenter', function() {
		summaryPopupHovered = true;
		if (summaryPopupTimeout) {
			clearTimeout(summaryPopupTimeout);
			summaryPopupTimeout = null;
		}
	});
	
	summaryPopupElement.on('mouseleave', function() {
		summaryPopupHovered = false;
		setTimeout(() => {
			if (!summaryIconHovered) {
				summaryPopupTimeout = setTimeout(() => {
					closePopup();
				}, SUMMARY_POPUP_HIDE_DELAY);
			}
		}, 50);
	});
	
	$(document).on('keydown.summary-popup', function(e) {
		if (e.key === 'Escape') closePopup();
	});
	
	setTimeout(() => {
		summaryPopupElement.addClass('summary-popup-overlay--visible');
	}, 10);
}

function hideSummaryPopup() {
	if (summaryPopupTimeout) {
		clearTimeout(summaryPopupTimeout);
		summaryPopupTimeout = null;
	}
	if (summaryHoverTimeout) {
		clearTimeout(summaryHoverTimeout);
		summaryHoverTimeout = null;
	}
	$(document).off('mousemove.summary-drag mouseup.summary-drag');
	summaryPopupHovered = false;
	summaryIconHovered = false;
	if (summaryPopupElement) {
		summaryPopupElement.removeClass('summary-popup-overlay--visible');
		setTimeout(() => {
			if (summaryPopupElement) {
				summaryPopupElement.remove();
				summaryPopupElement = null;
			}
		}, 200);
	}
}

function showSummaryErrorMessage(message) {
	hideSummaryPopup();
	
	const popupHtml = `
		<div class="summary-popup-overlay summary-popup-overlay--visible" id="summary-popup-overlay">
			<div class="summary-popup">
				<div class="summary-popup__header">
					<div class="summary-popup__title">Error</div>
					<button class="summary-popup__close" aria-label="Close">
						<i class="bi bi-x-lg"></i>
					</button>
				</div>
				<div class="summary-popup__content">
					<div class="text-error">${escapeHtml(message)}</div>
				</div>
			</div>
		</div>
	`;
	
	$('body').append(popupHtml);
	
	const popup = $('#summary-popup-overlay');
	
	const closePopup = () => {
		popup.remove();
		$(document).off('keydown.summary-popup');
	};
	
	popup.find('.summary-popup__close').on('click', closePopup);
	popup.on('click', function(e) {
		if ($(e.target).is('.summary-popup-overlay')) closePopup();
	});
	
	$(document).on('keydown.summary-popup', function(e) {
		if (e.key === 'Escape') closePopup();
	});
}

// State getters/setters for event handlers
function getSummaryState() {
	return {
		summaryPopupElement,
		summaryPopupTimeout,
		summaryHoverTimeout,
		currentSummaryFile,
		summaryPopupHovered,
		summaryIconHovered,
		SUMMARY_POPUP_HIDE_DELAY
	};
}

function setSummaryState(state) {
	summaryPopupElement = state.summaryPopupElement;
	summaryPopupTimeout = state.summaryPopupTimeout;
	summaryHoverTimeout = state.summaryHoverTimeout;
	currentSummaryFile = state.currentSummaryFile;
	summaryPopupHovered = state.summaryPopupHovered;
	summaryIconHovered = state.summaryIconHovered;
}

const SummaryPopupAPI = {
	updateSummaryContent,
	showSummaryPopup,
	hideSummaryPopup,
	getSummaryState,
	setSummaryState
};

if (typeof window !== 'undefined') {
	window.SummaryPopup = SummaryPopupAPI;
}